import {
    Directive,
    ElementRef,
    forwardRef,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Renderer2,
    SimpleChange,
    SimpleChanges
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Decimal} from 'decimal.js';
import * as _ from 'lodash-es';
import numeral from 'numeral';
import {correctNumber, createNumberFormat, DEFAULT_DECIMAL_SEPARATOR} from '../../common/formatters';
import NumberFormat = Intl.NumberFormat;

const DEFAULT_DECIMAL_NUMBERS_COUNT = 4;
const DEFAULT_STEP = 1;
const DEFAULT_CORRECT_VALUE = 0;

export const IS_INPUT_DISABLED_CLASS = '-disabled';

const INCLUDED_KEY_CODES = [
    'KeyA',
    'KeyC',
    'KeyX',
    'keyZ',
    'Tab',
    'ControlLeft',
    'ControlRight',
    'MetaLeft',
    'MetaRight',
    'OSLeft',
    'OSRight',
    'Backspace',
    'Delete',
    'Escape',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Up', // IE specific value
    'Down', // IE specific value
    'Del', // IE specific value
    'Esc' // IE specific value
];

const isExcludedKeyCode = (keyCode: string): boolean => !INCLUDED_KEY_CODES.includes(keyCode);

interface InputConstraints {
    isMinDefined: boolean;
    min: number;
    isMaxDefined: boolean;
    max: number;
    isStepDefined: boolean;
    step: number;
}

interface InputChanges extends SimpleChanges {
    decimal: SimpleChange;
    decimalRequired: SimpleChange;
    min: SimpleChange;
    max: SimpleChange;
    step: SimpleChange;
    precision: SimpleChange;
    allowEmpty: SimpleChange;
}

@Directive({
    selector: 'input[decimalInput]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DecimalInputDirective),
            multi: true
        }
    ],
    standalone: false
})
export class DecimalInputDirective implements ControlValueAccessor, OnChanges, OnInit {

    @HostBinding() // Is primary value binding
    public value: string = '';

    @Input()
    public decimalRequired: boolean = true;

    @Input()
    public decimal: number = DEFAULT_DECIMAL_NUMBERS_COUNT;

    @Input()
    public precision: number;

    @HostBinding(`class.${IS_INPUT_DISABLED_CLASS}`)
    @HostBinding('attr.disabled')
    public isDisabled: boolean = null; // Null is required in order to remove "disabled" attribute.

    @Input()
    public min: number;

    @Input()
    public max: number;

    @Input()
    public step: number;

    @Input()
    public allowEmpty: boolean;

    private numberFormat: NumberFormat;

    private isNgOnInitPassed: boolean = false;

    private isInFocus: boolean = false;

    /**
     * Raw number value, without formatting and any corrections
     */
    private rawValue: number;

    private propagateChange: (value: number) => void;

    private propagateOnTouched: () => void;

    constructor(
        private inputElementRef: ElementRef,
        private renderer: Renderer2
    ) {}

    public ngOnChanges(changes: InputChanges): void {

        // Detect that we have already passed OnInit phase.
        if (this.isNgOnInitPassed) {
            const isFormatParamsChanged = changes.decimal || changes.decimalRequired || changes.precision;

            if (isFormatParamsChanged) {
                this.updateNumberFormat();
            }

            /**
             * Note, we don't check here these changes:
             * {@link InputChanges.min}, {@link InputChanges.max}, {@link InputChanges.step},
             * because reformatting of the value, doesn't update model's value,
             * but updates display value, which can hide some problems, but not fix.
             */
            const isCorrectionNeeded = isFormatParamsChanged
                || changes.allowEmpty;

            if (isCorrectionNeeded) {
                const value = this.correctInputValue(this.rawValue);

                this.value = this.formatValueOnChange(value);
            }
        }
    }

    public ngOnInit(): void {
        this.isNgOnInitPassed = true;
        this.updateNumberFormat();
    }

    public writeValue(value: number): void {
        this.rawValue = value;
        this.value = this.formatValueOnChange(value);
        this.renderer.setProperty(this.inputElementRef.nativeElement, 'value', this.value);
    }

    public registerOnChange(fn: (value: any) => void): void {
        this.propagateChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.propagateOnTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled || null; // Null is required in order to remove "disabled" attribute.
    }

    public stepUp(): void {
        if (!this.isDisabled) {
            this.incrementInputValue(this.value);
        }
    }

    public stepDown(): void {
        if (!this.isDisabled) {
            this.decrementInputValue(this.value);
        }
    }

    @HostListener('focus')
    public onFocus(): void {
        this.isInFocus = true;

        this.propagateOnTouched();
    }

    // tslint:disable:cyclomatic-complexity
    @HostListener('keydown', [
        '$event',
        '$event.target'
    ])
    public onKeyDown(
        event: KeyboardEvent,
        target: EventTarget | null
    ): void {
        const element = target as HTMLInputElement;
        const inputValue = element.value;

        if (!this.isDisabled) {
            const modifier = event.ctrlKey || event.metaKey;
            const eventKeyDigit = parseInt(event.key, 0);
            const isDigitPressed = eventKeyDigit >= 0 && eventKeyDigit <= 9;
            const {selectionStart, selectionEnd} = element;
            const isSecondDecimalSeparatorPressed = (
                DEFAULT_DECIMAL_SEPARATOR === event.key
                && inputValue.includes(DEFAULT_DECIMAL_SEPARATOR)
                && !inputValue.substring(selectionStart, selectionEnd)
                    .includes(DEFAULT_DECIMAL_SEPARATOR)
            );
            const isAllowedMinusPressed = (
                event.key === '-'
                && selectionStart === 0
                && !inputValue.substring(selectionEnd).includes('-')
            );

            // Only digits, one decimal separator and included keys
            if (
                isSecondDecimalSeparatorPressed
                || !isDigitPressed
                && DEFAULT_DECIMAL_SEPARATOR !== event.key
                && !isAllowedMinusPressed
                && !modifier
                && isExcludedKeyCode(event.key)
            ) {
                event.preventDefault();
            } else {
                this.handleUpAndDownSteps(event);
            }
        }
    }

    @HostListener('keyup', [
        '$event',
        '$event.target'
    ])
    public onKeyUp(
        event: KeyboardEvent,
        target: EventTarget | null
    ): void {
        const element = target as HTMLInputElement;
        const inputValue = element.value;

        if (!this.isDisabled) {
            const numeralValue: number = numeral(inputValue).value();
            const [leftPart, rightPart] = inputValue.split(DEFAULT_DECIMAL_SEPARATOR);
            const decimalCount: number = rightPart !== undefined ? rightPart.length : 0;

            // numeral('23.33.456456').value() => NaN
            const isInputValueNotNumber: boolean = numeralValue === null || isNaN(numeralValue);

            if (!isInputValueNotNumber && leftPart.length > 3 && isExcludedKeyCode(event.key)) {
                const formattedValue: string = this.formatValue(numeralValue, decimalCount);
                const needToAddDecimalDelimiter: boolean = rightPart !== undefined && !rightPart.length;
                const rightValue: string = needToAddDecimalDelimiter
                    ? formattedValue + DEFAULT_DECIMAL_SEPARATOR
                    : formattedValue;
                let caretPositionStart: number = element.selectionStart;
                let caretPositionEnd: number = element.selectionEnd;

                if (rightValue.length > inputValue.length) {
                    caretPositionEnd++;
                    caretPositionStart = caretPositionEnd;
                } else if (rightValue.length < inputValue.length) {
                    caretPositionEnd--;
                    caretPositionStart = caretPositionEnd;
                }

                this.renderer.setProperty(
                    this.inputElementRef.nativeElement,
                    'value',
                    rightValue
                );
                element.setSelectionRange(caretPositionStart, caretPositionEnd);
            }
        }
    }

    @HostListener('blur', ['$event.target'])
    public onBlur(target: EventTarget | null): void {
        const inputValue = (target as HTMLInputElement).value;

        this.isInFocus = false;

        if (!this.isDisabled) {
            const correctValue: number = this.correctInputValue(inputValue);

            this.writeValue(correctValue);
            this.propagateChange(correctValue);
        }
    }

    @HostListener('wheel', [
        '$event',
        '$event.deltaY',
        '$event.target'
    ])
    public onWheel(
        event: WheelEvent,
        deltaY: number,
        target: EventTarget | null
    ): void {
        const inputValue = (target as HTMLInputElement).value;

        if (!this.isDisabled && this.isInFocus) {
            event.stopPropagation();
            event.preventDefault();

            if (deltaY < 0) {
                this.incrementInputValue(inputValue);
            } else if (deltaY > 0) {
                this.decrementInputValue(inputValue);
            }
        }
    }

    private updateNumberFormat(): void {
        this.numberFormat = createNumberFormat({
            isDecimalRequired: this.decimalRequired,
            decimalCount: this.decimal,
            precision: this.precision
        });
    }

    private getInputConstraints(): InputConstraints {
        const isMinDefined = _.isFinite(this.min);
        const min = isMinDefined ? this.min : null;

        const isMaxDefined = _.isFinite(this.max);
        const max = isMaxDefined ? this.max : null;

        const isStepDefined = _.isFinite(this.step);
        const step = isStepDefined ? this.step : null;

        return {
            isMinDefined,
            min,
            isMaxDefined,
            max,
            isStepDefined,
            step
        };
    }

    private correctInputValue(inputValue: string | number): number | undefined {
        let correctValue: number | undefined;

        if (!this.allowEmpty || inputValue || inputValue === 0) {
            correctValue = numeral(inputValue).value();

            const inputConstraints = this.getInputConstraints();

            correctValue = correctNumber(
                correctValue,
                inputConstraints.isMinDefined ? inputConstraints.min : correctValue,
                inputConstraints.isMaxDefined ? inputConstraints.max : correctValue,
                inputConstraints.step,
                inputConstraints.isMinDefined ? inputConstraints.min : DEFAULT_CORRECT_VALUE
            );
        }

        return correctValue;
    }

    private incrementInputValue(inputValue: string, incrementSign: 1 | -1 = 1): void {
        let oldValue: number = this.correctInputValue(inputValue);
        const inputConstraints = this.getInputConstraints();
        let newValue: number | undefined;

        if (oldValue === undefined) {
            oldValue = incrementSign < 0 && inputConstraints.isMinDefined
                ? inputConstraints.min
                : incrementSign > 0 && inputConstraints.isMaxDefined
                    ? inputConstraints.max
                    : oldValue;
        }

        if (oldValue !== undefined) {
            newValue = this.correctInputValue(
                new Decimal(oldValue).add(
                    new Decimal(inputConstraints.step || DEFAULT_STEP).mul(incrementSign)
                ).toNumber()
            );
        }

        this.writeValue(newValue);
        this.propagateChange(newValue);
    }

    private decrementInputValue(inputValue: string): void {
        this.incrementInputValue(inputValue, -1);
    }

    private formatValue(value: number, decimalCount: number): string {
        const isDecimalRequired = true;
        const precision = this.precision;

        return createNumberFormat({isDecimalRequired, decimalCount, precision}).format(value);
    }

    private formatValueOnChange(value: number): string {
        return _.isFinite(value)
            ? this.numberFormat.format(value)
            : '';
    }

    private handleUpAndDownSteps(event: KeyboardEvent): void {
        switch (event.key) {
            case 'Up': // IE specific value
            case 'ArrowUp':
                event.preventDefault();
                this.stepUp();
                break;

            case 'Down': // IE specific value
            case 'ArrowDown':
                event.preventDefault();
                this.stepDown();
                break;
        }
    }
}
